import React, { useState } from "react";
import PropTypes from "prop-types";
import { graphql } from "gatsby";

import { classNames } from "@/helpers/classNames";

import LinkWrapper from "@/components/linkWrapper";
import MediaWrapper from "@/components/mediaWrapper";
import MarkdownWrapper from "@/components/markdownWrapper";
import ImageViewerModal from "@/components/ImageViewerModal";

export const query = graphql`
  fragment FeaturesWithVariableImageData on StrapiComponentFeaturesWithVariableImage {
    title
    topic
    image {
      ...StrapiMediaSubquery
    }
    imageIsRight
    useImageViewer
    body {
      ...StrapiSharedBody
    }
    items {
      title
      description
      icon {
        ...StrapiMediaSubquery
      }
      link {
        url
      }
    }
    testimonial {
      image {
        ...StrapiMediaSubquery
      }
      name
      jobTitle
      text
    }
    buttons {
      ...StrapiSharedLink
    }
  }
`;

function WithVariableImage({ data }) {
  if (!data) {
    return <template />;
  }

  const [showBigImage, setShowBigImage] = useState(false);

  const handleImageClick = () => setShowBigImage(!showBigImage);

  return (
    <div className="overflow-hidden bg-background-white px-4 py-12 sm:px-6 lg:px-0">
      <div className="mx-auto lg:grid lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-2 lg:gap-24 lg:px-8">
        <div
          className={classNames(
            "mx-auto max-w-xl lg:mx-0 lg:max-w-none lg:py-16",
            data.imageIsRight ? "" : "order-2"
          )}
        >
          <div>
            <div className="mt-6">
              {data.topic && (
                <h3 className="block text-base font-semibold uppercase tracking-wide text-text-highlight">
                  <MarkdownWrapper text={data.topic} />
                </h3>
              )}
              {data.title && (
                <h2 className="text-3xl font-extrabold tracking-tight text-text-primary">
                  <MarkdownWrapper text={data.title} />
                </h2>
              )}
              {data.body?.text && (
                <MarkdownWrapper
                  text={data.body.text}
                  images={data.body.images}
                  markdownClassName="mt-4 text-lg text-text-secondary prose"
                  replaceParagraph={false}
                />
              )}
              {Boolean(data.items?.length) && (
                <dl>
                  {data.items.map((item, index) => (
                    <LinkWrapper
                      key={index}
                      to={item?.link?.url}
                      className={`-mx-2 mt-6 flex p-2 ${
                        item.link?.url
                          ? "rounded-md hover:bg-background-light-gray"
                          : ""
                      }`}
                    >
                      {item.icon ? (
                        <MediaWrapper
                          media={item.icon}
                          objectFit="contain"
                          divClassName="w-24"
                          aria-hidden="true"
                        />
                      ) : (
                        <div className="w-24 flex-none" />
                      )}
                      <div>
                        {item.title && (
                          <dt>
                            <MarkdownWrapper
                              text={item.title}
                              markdownClassName="ml-4 text-lg leading-6 font-medium text-text-primary"
                            />
                          </dt>
                        )}
                        {item.description && (
                          <dd className="prose ml-4 mt-2 text-base text-text-secondary">
                            <MarkdownWrapper text={item.description} />
                          </dd>
                        )}
                      </div>
                    </LinkWrapper>
                  ))}
                </dl>
              )}
              {Boolean(data.buttons?.length) && (
                <div className="flex">
                  {data.buttons.map(
                    (button, index) =>
                      button?.link?.url &&
                      (button?.overwriteTitle || button?.link?.title) && (
                        <div key={index} className="mr-4 mt-6">
                          <LinkWrapper
                            to={button.link.url}
                            className="inline-flex rounded-md border border-transparent bg-background-primary px-4 py-2 text-base font-medium text-text-white shadow-sm hover:bg-background-primary-dark"
                          >
                            {button.overwriteTitle || button.link.title}
                          </LinkWrapper>
                        </div>
                      )
                  )}
                </div>
              )}
            </div>
          </div>
          {data.testimonial && (
            <div className="border-border-light mt-8 border-t pt-6">
              <blockquote>
                {data.testimonial.text && (
                  <div>
                    <MarkdownWrapper
                      text={`&ldquo;${data.testimonial.text}&rdquo;`}
                      markdownClassName="text-base text-text-secondary"
                      replaceParagraph={false}
                    />
                  </div>
                )}
                <footer className="mt-3">
                  <div className="flex items-center space-x-3">
                    {data.testimonial.image && (
                      <div className="flex-shrink-0">
                        <MediaWrapper
                          media={data.testimonial.image}
                          divClassName="h-6 w-6"
                          className="rounded-full"
                        />
                      </div>
                    )}
                    {data.testimonial.name && (
                      <div className="text-base font-medium text-text-secondary-dark">
                        {data.testimonial.name}
                      </div>
                    )}
                  </div>
                </footer>
              </blockquote>
            </div>
          )}
        </div>
        <div className={classNames("", data.imageIsRight ? "" : "order-1")}>
          {data.image && (
            <div className="mx-auto mt-12 flex aspect-[1/1] max-w-xl items-center sm:mt-16 lg:mx-0 lg:mt-0 lg:aspect-[auto] lg:h-0 lg:min-h-full lg:max-w-none">
              <div className="h-full w-full">
                {data.useImageViewer ? (
                  <>
                    <button onClick={handleImageClick}>
                      <span className="sr-only">Open image viewer</span>
                      <MediaWrapper
                        media={data.image}
                        divClassName="h-full w-full"
                        videoClassName="w-full"
                        objectFit="contain"
                        controls
                      />
                    </button>
                    <ImageViewerModal
                      image={data.image}
                      isOpen={showBigImage}
                      setIsOpen={setShowBigImage}
                    />
                  </>
                ) : (
                  <MediaWrapper
                    media={data.image}
                    divClassName="h-full w-full"
                    videoClassName="w-full"
                    objectFit="contain"
                    controls
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

WithVariableImage.propTypes = {
  data: PropTypes.object,
};

export default WithVariableImage;
